import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import jwtAxios from "@crema/services/auth/jwt-auth";
import {
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import AppInfoView from "@crema/core/AppInfoView";
import AppsContainer from "@crema/core/AppsContainer";
import AppsPagination from "@crema/core/AppsPagination";
import AppsContent from "@crema/core/AppsContainer/AppsContent";
import AppsHeader from "@crema/core/AppsContainer/AppsHeader";
import { AppState } from "redux/store";
import PlanTable from "./PlanTable";
import { createPlanGroupCode, getPlan } from "redux/actions/planMaster";

const Plan = () => {
  const { messages } = useIntl();
  const dispatch = useDispatch();

  const rowsPerPage = 50;
  const [page, setPage] = useState(0);
  const [search, setSearchQuery] = useState("");
  const [searchField, setSearchField] = useState("all");
  const [openDialog, setOpenDialog] = useState(false);
  const [groupCode, setGroupCode] = useState("");
  const [selectedPlanIds, setSelectedPlanIds] = useState<string[]>([]);

  const { plan, planCount } = useSelector<AppState, AppState["planMaster"]>(
    ({ planMaster }) => planMaster
  );

  const offset = page * rowsPerPage;

  useEffect(() => {
    const trimmed = search.trim();
    const field = searchField === "all" ? "" : searchField;
    dispatch(getPlan(trimmed, offset, rowsPerPage, field));
  }, [dispatch, search, page, searchField]);


  const onSearchPlan = (value: string) => {
    const trimmed = value.trim();
    const field = searchField === "all" ? "" : searchField;
    setSearchQuery(trimmed);
    setPage(0);
    dispatch(getPlan(trimmed, 0, rowsPerPage, field));
  };


  const searchFields = [
    { label: "All", value: "all" },
    { label: "Plan Name", value: "planName" },
    { label: "Plan Group Code", value: "planGroupCode" },
    { label: "Plan Duration", value: "planDuration" },
    { label: "Base Price", value: "rentExclTax" },
    { label: "Popular Plan", value: "popPlan" },
  ];

  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => setOpenDialog(false);

  const handleSaveGroupCode = () => {
    const payload = {
      planIds: selectedPlanIds.join(","),
      planGroupCode: groupCode,
    };

    dispatch(
      createPlanGroupCode(payload, () => {
        dispatch(getPlan("", offset, rowsPerPage));
        setSelectedPlanIds([]);
      })
    );

    setGroupCode("");
    setOpenDialog(false);
  };

  const exportPlansToCSV = async () => {
    const response = await jwtAxios.get("/plans/download/csv", {
      responseType: "blob",
    });
    const blob = new Blob([response.data], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "plans.csv";
    link.click();
    URL.revokeObjectURL(link.href);
  };


  return (
    <>
      <AppsContainer
        title={
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              component="span"
              sx={{
                fontSize: 16,
                fontWeight: "bold",
                color: "text.primary",
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {messages["sidebar.planMaster"]}
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                color="secondary"
                onClick={exportPlansToCSV}
              >
                Export
              </Button>
            </Box>
          </Box>
        }
        fullView
      >
        <AppsHeader>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: 1,
              gap: 2,
            }}
          >
            <Box>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleOpenDialog}
                disabled={selectedPlanIds.length === 0}
              >
                Add Group Code
              </Button>
            </Box>

            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <TextField
                select
                size="small"
                value={searchField}
                onChange={(e) => {
                  setSearchField(e.target.value);
                  setSearchQuery("");
                  setPage(0);
                }}
                SelectProps={{ native: true }}
              >
                {searchFields.map((field) => (
                  <option key={field.value} value={field.value}>
                    {field.label}
                  </option>
                ))}
              </TextField>

              <TextField
                variant="outlined"
                size="small"
                color="primary"
                value={search}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    onSearchPlan(search);
                  }
                }}
                placeholder={messages["common.searchHere"] as string}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {search ? (
                        <IconButton
                          onClick={() => {
                            setSearchQuery("");
                            onSearchPlan("");
                          }}
                          size="small"
                        >
                          <CloseIcon />
                        </IconButton>
                      ) : (
                        <SearchIcon
                          onClick={() => onSearchPlan(search)}
                          sx={{ cursor: "pointer" }}
                        />
                      )}
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 200 }}
              />

            </Box>
          </Box>
        </AppsHeader>

        <AppsContent
          sx={{
            paddingTop: 2.5,
            paddingBottom: 2.5,
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
          }}
        >
          <PlanTable
            plan={plan}
            selectedPlanIds={selectedPlanIds}
            onSelectionChange={setSelectedPlanIds}
          />
          <AppsPagination
            rowsPerPage={rowsPerPage}
            count={planCount}
            page={page}
            onPageChange={(e, value) => setPage(value)}
          />
        </AppsContent>
      </AppsContainer>

      <AppInfoView />

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Add Group Code</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Group Code"
            type="text"
            fullWidth
            value={groupCode}
            onChange={(e) => setGroupCode(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleSaveGroupCode}
            color="primary"
            variant="contained"
            disabled={!groupCode.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Plan;
